<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import type { Component } from 'vue';
import UiButton from './UiButton.vue';

export type UiIconTextActionLayout = 'stacked' | 'inline';

export interface UiIconTextActionButton {
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger';
  label: string;
  disabled?: boolean;
  iconPosition?: 'left' | 'right' | 'top' | 'bottom';
  icon?: string;
  customIcon?: Component;
}

interface Props {
  title: string;
  subtitle: string;
  iconComponent?: Component;
  button?: UiIconTextActionButton;
  buttonLayout?: UiIconTextActionLayout;
  loading?: boolean;
}

defineOptions({ name: 'UiIconTextAction' });

const {
  iconComponent = undefined,
  button = undefined,
  buttonLayout = 'stacked',
  loading = false,
} = defineProps<Props>();

const emit = defineEmits<{
  action: [];
}>();
</script>

<template>
  <div class="ui-icon-text-action" :class="`ui-icon-text-action--${buttonLayout}`">
    <span v-if="iconComponent" class="ui-icon-text-action__icon">
      <component :is="iconComponent" />
    </span>

    <div class="ui-icon-text-action__content">
      <div class="ui-icon-text-action__text">
        <h6 class="ui-icon-text-action__title">{{ title }}</h6>
        <p class="ui-icon-text-action__subtitle">{{ subtitle }}</p>
      </div>

      <ProgressSpinner
        v-if="loading"
        class="ui-icon-text-action__spinner"
        stroke-width="6"
        aria-label="Loading"
      />

      <UiButton
        v-else-if="button"
        :severity="button.severity || 'primary'"
        :label="button.label"
        :disabled="button.disabled"
        :icon="button.customIcon ? undefined : button.icon"
        :icon-pos="button.iconPosition"
        class="ui-icon-text-action__button"
        size="s"
        @click="emit('action')"
      >
        <template v-if="button.customIcon && !button.icon" #icon>
          <component :is="button.customIcon" />
        </template>
      </UiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-icon-text-action {
  display: flex;
  align-items: flex-start;
  gap: $spacing-md;
  width: 100%;
}

.ui-icon-text-action__icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: $radius-999;
  background-color: $color-brand-700;
  color: $color-icon-white;

  :deep(svg) {
    width: $icon-size-sm;
    height: $icon-size-sm;
  }
}

.ui-icon-text-action__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: $spacing-sm;
  min-width: 0;
}

.ui-icon-text-action__text {
  display: flex;
  flex-direction: column;
  gap: $spacing-sm;
}

.ui-icon-text-action__title {
  margin: 0;
  font-size: $font-size-md;
  font-weight: $font-weight-semibold;
  line-height: 1;
  color: $color-text-inverse;
}

.ui-icon-text-action__subtitle {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: $font-weight-regular;
  line-height: 1.2;
  color: $color-text-inverse;
}

.ui-icon-text-action__button {
  align-self: flex-start;
}

.ui-icon-text-action__spinner {
  width: $static-icon-xs;
  height: $static-icon-xs;
}

.ui-icon-text-action--stacked .ui-icon-text-action__spinner {
  display: block;
  margin-inline: auto;
}

.ui-icon-text-action--inline .ui-icon-text-action__content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing-s;
}
</style>
